import axios from 'axios';
import store from '@/store/store'
const instance = axios.create();
import { getItem } from '@/utils/db';

// instance.defaults.baseURL = 'https://api.example.com';
instance.defaults.baseURL = 'https://election-su.mfu.ac.th';  // Production server
// instance.defaults.baseURL = 'http://127.0.0.1:8030';  
// instance.defaults.baseURL = 'http://127.0.0.1:8081';
// instance.defaults.timeout = 5000;

instance.defaults.headers = {
  "Content-Type": "application/json",
};

function setAuthToken(token) {
  if (token) {
    instance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete instance.defaults.headers.common["Authorization"];
  }
}

instance.interceptors.request.use(
  async (config) => {
    // If a header is already set via setAuthToken, skip reading storage each time (optional)
    if (!config.headers.Authorization) {
      const token = await getItem("accessToken"); // works with IndexedDB/localStorage
      if (token) config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor: handle 401/500
instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;

    if (status === 401) {
      // Clear storage and header
      await removeItem("accessToken");
      await removeItem("user");
      localStorage.removeItem("loginSource");
      setAuthToken(null);

      // Optional: show a toast here

      // Redirect to login
      router.push("/login");
    } else if (status === 500) {
      console.error("Server error. Please try again later.");
    }
    return Promise.reject(error);
  }
);

export default {
  // Allow store to set/remove token immediately
  // setAuthToken,

  // Persist token to storage and header
  onSetToken: async (token) => {
    if (token) {
      await setItem("accessToken", token);
      setAuthToken(token);
    } else {
      await removeItem("accessToken");
      setAuthToken(null);
    }
  },

  authenticated(method, data, configs) {
    console.log('API: authenticated', method, data, configs)
    switch (method) {
      case 'signup':
        return instance.post("/api/v1/signup", data);
      case 'signin':
        return instance.post("/api/v1/signin", data);
      case 'update-major':
        return instance.put(`/api/v1/account/${data.id}/major`, { major: data.major });
      case 'message':
        return instance.get("/api/v1/setting/auth/message", data);
      case 'create-message':
        return instance.post("/api/v1/setting/auth/message", data);
      case 'update-message':
        return instance.put("/api/v1/setting/auth/message", data);
      case 'remove-message':
        return instance.delete("/api/v1/setting/auth/message", data);
      default:
        break;
    }
  },

  members(method, data, configs) {
    switch (method) {
      case 'exp':
        return instance.post("/api/v1/explore/profile", data);
      case 'get':
        return instance.get("/api/v1/account");
      case 'getByStudentID':
        return instance.get(`/api/v1/student/${data}`);
      case 'post': {
        if (data) delete data._id;
        return instance.post("/api/v1/account/checkID", data);

      }
      default:
        throw new Error(`Unknown members method: ${method}`);
    }
  },


  roles(method, data, configs) {
    switch (method) {
      case 'exp':
        return instance.get("/api/v1/roles", data);
      case 'get':
        return instance.get("/api/v1/roles");
      case 'getOne':
        return instance.get("/api/v1/role");
      case 'post':
        return instance.post("/api/v1/roles", data);
      case 'put':
        return instance.put("/api/v1/role", data);
      case 'getAccountsByRole':
        return instance.get(`/api/v1/role/${data}/accounts`);
      case 'delete':
        return instance.delete("/api/v1/accounts", {
          data: { id: data.id }
        });
      default:
        break;
    }
  },

  campus(method, data, configs) {
    switch (method) {
      case 'exp':
        return instance.post("/api/v1/explore/campus", data);
      case 'get':
        return instance.get("/api/v1/campus");
      case 'post':
        delete data._id;
        return instance.post("/api/v1/campus", data);
      case 'put':
        return instance.put("/api/v1/campus", data);
      case 'delete':
        return instance.delete("/api/v1/campus");
      default:
        break;
    }
  },
  major(method, data, configs) {
    switch (method) {
      case 'get':
        return instance.get("/api/v1/major");
      case 'post':
        delete data._id;
        return instance.post("/api/v1/major", data);
      case 'put':
        return instance.put("/api/v1/major", data);
      case 'delete':
        return instance.delete("/api/v1/major");
      default:
        break;
    }
  },
  department(method, data, configs) {
    switch (method) {
      case 'exp':
        return instance.post("/api/v1/departments", data);
      case 'get':
        return instance.get("/api/v1/department");
      case 'post':
        delete data._id;
        return instance.post("/api/v1/department", data);
      case 'put':
        return instance.put("/api/v1/department", data);
      case 'delete':
        return instance.delete("/api/v1/department");
      default:
        break;
    }
  },

  verification(method, data, configs) {
    switch (method) {
      case 'exp':
        return instance.get("/api/v1/setting/verification", data);
      case 'get':
        return instance.get("/api/v1/setting/verification");
      case 'post':
        delete data._id;
        return instance.post("/api/v1/setting/verification", data);
      case 'put':
        return instance.put("/api/v1/setting/verification", data);
      case 'delete':
        return instance.delete("/api/v1/setting/verification");
      default:
        break;
    }
  },

  pdpa(method, payload = {}, configs = {}) {
    switch (method) {
      case 'get':
        return instance.get(`/api/v1/pdpa`);
      case 'post':
        delete payload._id;
        return instance.post(`/api/v1/pdpa`, payload);
      case 'put':
        return instance.put(`/api/v1/pdpa`, payload);
      case 'delete':
        return instance.delete(`/api/v1/pdpa`, { data: payload });
      case 'addPictures': {
        const { id, data } = payload;
        return instance.put(`/api/v1/pdpa/add-pictures/${id}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
          ...configs
        });
      }
      case 'removePicture': {
        const { id, image } = payload;
        return instance.put(`/api/v1/pdpa/remove-picture/${id}`, { image }, configs);
      }
      default:
        throw new Error(`Unknown pdpa method: ${method}`);
    }
  },

  condition(method, data, configs) {
    switch (method) {
      case 'exp':
        return instance.get("/api/v1/condition", data);
      case 'get':
        return instance.get("/api/v1/condition");
      case 'post':
        delete data._id;
        return instance.post("/api/v1/condition", data);
      case 'put':
        return instance.put("/api/v1/condition", data);
      case 'delete':
        return instance.put("/api/v1/condition", data);
      case 'deleteConditionPicture': {
        return instance.delete(`/api/v1/condition/picture/${data.id}`, {
          data: { imagePath: data.imagePath }
        })
      }
      case 'putConditionPictures': {
        const formData = new FormData();
        for (const img of data.images) {
          formData.append('images', img);
        }
        return instance.put(`/api/v1/condition/pictures/${data.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          ...configs
        });
      }
      default:
        break;
    }
  },

  slide(method, data, configs) {
    switch (method) {
      case 'exp':
        return instance.get("/api/v1/slide", data);
      case 'get':
        return instance.get("/api/v1/slide");
      case 'post':
        delete data._id;
        return instance.post("/api/v1/slide", data);
      case 'put':
        return instance.put("/api/v1/slide", data);
      case 'delete':
        return instance.put("/api/v1/slide", data);
      case 'putslidePicture': {
        const formData = new FormData();
        formData.append('image', data.image);
        return instance.put(`/api/v1/slide/picture/${data.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          ...configs
        });
      }
      default:
        break;
    }
  },

  candidate(method, data, configs) {
    switch (method) {
      case 'get':
        return instance.get(`/api/v1/candidate/${data.id}`);
      case 'getCandidates':
        return instance.get('/api/v1/candidates');
      case 'getAll':
        return instance.get('/api/v1/candidates/all');
      case 'post':
        delete data._id;
        return instance.post('/api/v1/candidate', data);
      case 'adminAdd':
        return instance.post("/api/v1/candidate/admin-add", data);
      case 'getMyCandidateByAccountID':
        return instance.post('/api/v1/candidate/getMyCandidateByAccountID', data, configs);
      case 'put':
        return instance.put('/api/v1/candidate', data);
      case 'delete':
        return instance.delete('/api/v1/candidate', { data });
      case 'changeCandidateStatus':
        return instance.put('/api/v1/candidate/change', data, {
          headers: { 'Content-Type': 'application/json' }
        });
      case 'checkRegistered':
        return instance.post('/api/v1/candidate/check', data, configs);
      case 'uploadCandidatePicture':
        return instance.put(`/api/v1/candidate/${data.id}/picture`, data.formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          ...configs
        });
      case 'putCandidateTranscript':
        return instance.put(`/api/v1/candidate/${data.id}/transcript`, data.formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          ...configs
        });
      default:
        throw new Error(`Unknown candidate method: ${method}`)
    }
  },


  gpax(method, data, configs) {
    switch (method) {
      case 'get':
        return instance.get("/api/v1/gpax");
      case 'post':
        delete data._id;
        return instance.post("/api/v1/gpax", data);
      case 'put':
        return instance.put("/api/v1/gpax", data);
      case 'delete':
        return instance.delete("/api/v1/gpax");
      default:
        break;
    }
  },

  status(method, data, configs) {
    switch (method) {
      case 'get':
        return instance.get("/api/v1/status");
      case 'post':
        delete data._id;
        return instance.post("/api/v1/status", data);
      case 'put':
        return instance.put("/api/v1/status", data);
      case 'delete':
        return instance.delete("/api/v1/status");
      default:
        break;
    }
  },

  type(method, data, configs) {
    switch (method) {
      case 'get':
        return instance.get("/api/v1/type");
      case 'post':
        delete data._id;
        return instance.post("/api/v1/type", data);
      case 'put':
        return instance.put("/api/v1/type", data);
      case 'delete':
        return instance.delete("/api/v1/type");
      default:
        break;
    }
  },

  vote(method, data, configs) {
    switch (method) {
      case 'exp':
        return instance.get("/api/v1/vote/vote", data);
      case 'get':
        return instance.get("/api/v1/vote");
      case 'getAll':
        return instance.get("/api/v1/voteall");
      case 'check':
        return instance.get('/api/v1/vote/check', { params: data, ...configs })
      case 'sendVote': {
        const payload = { ...data }
        delete payload._id
        console.log('API: POST /api/v1/vote/send', payload)
        return instance.post('/api/v1/vote/send', payload, configs)
      }
      case 'abstain': {
        return instance.post('/api/v1/vote/abstain', data, configs)
      }
      case 'getVoteCounts':
        return instance.get("/api/v1/vote/count");
      default:
        throw new Error(`Unknown vote method: ${method}`);
    }
  },

  event(method, data, configs) {
    switch (method) {
      case 'get':
        return instance.get("/api/v1/event");
      case 'post':
        delete data._id;
        return instance.post("/api/v1/event", data);
      case 'put':
        return instance.put("/api/v1/event", data);
      case 'delete':
        return instance.delete("/api/v1/event");
      default:
        break;
    }
  },

  setter(method, data, configs) {
    switch (method) {
      case 'get':
        return instance.get("/api/v1/setter");
      case 'post':
        delete data._id;
        return instance.post("/api/v1/setter", data);
      case 'put':
        return instance.put("/api/v1/setter", data);
      case 'delete':
        return instance.delete('/api/v1/setter', { data: { id: data.id } })
      case 'checkEvent':
        return instance.get("/api/v1/setter/checkevent", configs);
      case 'checkSetter':
        return instance.post("/api/v1/setter/checksetter", data, configs);
      default:
        break;
    }


  },


}

